// Основные типы API
export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

// Аутентификация
export interface User {
  id: number;
  username: string;
  email: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  roleId: number;
  role: Role;
  isActive: boolean;
  lastLogin?: string;
  createdAt: string;
  updatedAt: string;
}

export interface MiniUser {
  id: number;
  username: string;
  email: string;
  firstName?: string | null;
  lastName?: string | null;
  role?: { name?: string } | null;
}

export interface Role {
  id: number;
  name: string;
  description?: string;
  permissions?: Record<string, any>;
}

/** Ответ GET /roles для страницы «Роли и доступ» */
export interface PermissionCatalogItem {
  key: string;
  label: string;
}

export interface PermissionCatalogGroup {
  moduleKey: string;
  title: string;
  permissions: PermissionCatalogItem[];
}

export interface RolesAccessPayload {
  roles: Role[];
  catalog: PermissionCatalogGroup[];
  whitelistKeys: string[];
}

export interface AuthState {
  user: User | null;
  token: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

export interface LoginRequest {
  username: string;
  password: string;
}

export interface LoginResponse {
  accessToken: string;
  refreshToken: string;
  user: User;
}

// Заявки
export interface Application {
  id: number;
  internalNumber: string;
  externalNumber: string;
  applicationDate: string;
  contractType?: 'CLIENT' | 'SIMPLE';
  clientId: number;
  expeditorId?: number;
  shipperId?: number;
  consigneeId?: number;
  plannedLoadingDate?: string;
  plannedUnloadingDate?: string;
  /** Aggregated MIN across linked non-cancelled routes (Route.actualLoadingDate). */
  actualLoadingDate?: string | null;
  /** Aggregated MAX across linked non-cancelled routes (Route.actualUnloadingDate). */
  actualUnloadingDate?: string | null;
  status: ApplicationStatus;
  requiredConditions?: string;
  totalCostWithVAT?: number;
  loadingAddress?: string;
  loadingCity?: string | null;
  loadingContactPerson?: string;
  loadingPhone?: string;
  unloadingAddress?: string;
  unloadingCity?: string | null;
  unloadingContactPerson?: string;
  unloadingPhone?: string;
  partnerPaymentAmount?: number;
  partnerPaymentDate?: string;
  partnerPaymentStatus: string;
  originalsReceivedAt?: string;
  primaryDocsSignedDate?: string;
  primaryDocsStatus: string;
  clientRate?: number;
  applicationBasis?: string | null; // PARTNER_CONTRACT, CLIENT_CONTRACT, CLIENT_CONTRACT_REQUEST
  basisNumber?: string | null;
  basisDate?: string | null;
  prepaymentCondition?: string | null;
  mainPaymentCondition?: string | null;
  /** Условия договора (как в БД) */
  paymentMethod?: string | null;
  prepaymentPercent?: number | null;
  mainPaymentPercent?: number | null;
  prepaymentDelayDays?: number;
  mainPaymentDelayDays?: number;
  /** Связанный договор (основание «По договору») */
  contractId?: number | null;
  contract?: ApplicationContract | null;
  prepaymentReceivedDate?: string | null;
  mainPaymentReceivedDate?: string | null;
  responsibleLogistId?: number | null;
  responsibleLogist?: MiniUser | null;
  createdBy: number;
  createdAt: string;
  updatedAt: string;
  client: Client;
  expeditor?: Expeditor;
  shipper?: Shipper;
  consignee?: Consignee;
  createdByUser: User;
  cargo: ApplicationCargo[];
  documents: Document[];
  payments: Payment[];
  routes: RouteApplication[];
  /** Дебиторка клиента (INCOME/CLIENT), с бэкенда */
  financialObligations?: ApplicationClientFinancialObligation[];
  conditions?: ApplicationCondition[];
}

export type ApplicationConditionType =
  | 'LOADING_FAILURE'
  | 'UNLOADING_FAILURE'
  | 'LATE_LOADING'
  | 'LATE_UNLOADING'
  | 'DOCUMENTS'
  | 'VEHICLE_CHANGE'
  | 'NO_LOCATION';

export interface ApplicationCondition {
  id?: number;
  applicationId?: number;
  type: ApplicationConditionType;
  calculationType?: 'fixed' | 'percent' | 'per_day' | null;
  valueType?: 'fixed' | 'percent' | null;
  value?: number | string | null;
  thresholdDays?: number | null;
  deadlineDays?: number | null;
  comment?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

/** Строка FinancialObligation для карточки заявки (клиент) */
export interface ApplicationClientFinancialObligation {
  id: number;
  direction: string;
  counterpartyType: string;
  stage: string;
  status: string;
  amountWithVAT: number | string;
  baseDate?: string | null;
  dueDate?: string | null;
  appliedAmount?: number | string | null;
  payments?: Array<{ amountPaid?: number | string | null; paymentDate?: string | null }>;
  paymentAllocations?: Array<{ amount?: number | string | null }>;
  /** С бэкенда GET /applications/:id (маппинг) */
  planned_amount?: number;
  paid_amount?: number;
  remaining_amount?: number;
  delay_days?: number;
  is_active?: boolean;
}

/** Минимальные поля договора с бэкенда */
export interface ApplicationContract {
  id: number;
  number: string;
  date: string;
  clientId: number;
  prepaymentPercent: number | null;
  prepaymentDelayDays: number | null;
  postpaymentPercent: number | null;
  postpaymentDelayDays: number | null;
  paymentTermsText?: string | null;
  createdAt: string;
}

export enum ApplicationStatus {
  CREATED = 'CREATED',
  IN_WORK = 'IN_WORK',
  COMPLETED = 'COMPLETED',
  CANCELLED = 'CANCELLED',
  OVERDUE = 'OVERDUE'
}

export interface CreateApplicationRequest {
  externalNumber: string;
  applicationDate?: string;
  clientId: number;
  expeditorId?: number;
  shipperId?: number;
  consigneeId?: number;
  responsibleLogistId?: number | null;
  
  // Конкретные адреса для данной заявки
  loadingAddress?: string;
  loadingCity?: string;
  loadingContactPerson?: string;
  loadingPhone?: string;
  unloadingAddress?: string;
  unloadingCity?: string;
  unloadingContactPerson?: string;
  unloadingPhone?: string;
  
  plannedLoadingDate?: string;
  plannedUnloadingDate?: string;
  totalCostWithVAT?: number;
  requiredConditions?: string;
  
  // Поля основания заявки
  applicationBasis?: string | null; // PARTNER_CONTRACT, CLIENT_CONTRACT, CLIENT_CONTRACT_REQUEST
  basisNumber?: string | null;
  basisDate?: string | null;
  
  // Финансовые поля для своих заявок
  clientRate?: number;
  paymentMethod?: string;
  prepaymentCondition?: string;
  prepaymentPercent?: number;
  mainPaymentPercent?: number;
  prepaymentDelayDays?: number;
  mainPaymentDelayDays?: number;
  contractId?: number | null;
  conditions?: ApplicationCondition[];
  
  cargo: CreateCargoRequest[];
}

export interface CreateCargoRequest {
  name: string;
  description?: string;
  quantity: number;
  weightPerItem: number;
  lengthM?: number;
  widthM?: number;
  heightM?: number;
  totalWeight?: number;
  totalVolume?: number;
  packagingType?: string;
  cargoCharacter?: string;
}

// Справочники
export interface Client {
  id: number;
  name: string;
  shortName?: string | null;
  address?: string;
  contactPerson?: string;
  phone?: string;
  inn?: string;
  createdAt: string;
}

export interface Expeditor {
  id: number;
  name: string;
  address?: string;
  contactPerson?: string;
  phone?: string;
  createdAt: string;
}

export interface Shipper {
  id: number;
  name: string;
  address?: string;
  contactPerson?: string;
  phone?: string;
  inn?: string;
  createdAt: string;
}

export interface Consignee {
  id: number;
  name: string;
  address?: string;
  contactPerson?: string;
  phone?: string;
  inn?: string;
  createdAt: string;
}

export type VehicleCategoryCode = 'TRUCK' | 'TRAILER';

export interface Driver {
  id: number;
  fullName: string;
  phone?: string;
  passportData?: string;
  driverLicense?: string;
  isCompanyDriver?: boolean;
  defaultVehicleId?: number;
  defaultTrailerVehicleId?: number | null;
  defaultVehicle?: {
    id: number;
    registrationNumber: string;
    vehicleCategory?: VehicleCategoryCode;
    brand?: string;
    vehicleType?: string;
    wheelFormula?: string;
  } | null;
  defaultTrailerVehicle?: {
    id: number;
    registrationNumber: string;
    vehicleCategory?: VehicleCategoryCode;
    brand?: string;
    vehicleType?: string;
  } | null;
  assignedLogistId?: number | null;
  assignedLogist?: {
    id: number;
    username?: string;
    email?: string;
  } | null;
  carrierId?: number | null;
  carrier?: {
    id: number;
    name: string;
  } | null;
  createdAt: string;
}

export interface Vehicle {
  id: number;
  registrationNumber: string;
  /** Тягач или прицеп (без связи в паре) */
  vehicleCategory?: VehicleCategoryCode;
  /** У прицепа — тип прицепа; у старых записей могло быть произвольное описание */
  vehicleType?: string;
  /** У тягача — колёсная формула */
  wheelFormula?: string;
  brand?: string;
  carryingCapacity?: number;
  volumeM3?: number;
  /** У прицепа — размеры текстом */
  dimensionsText?: string;
  isCompanyVehicle?: boolean;
  driversDefault?: Array<{
    id: number;
    fullName: string;
  }>;
  createdAt: string;
}

export interface Carrier {
  id: number;
  name: string;
  contactPerson?: string;
  phone?: string;
  inn?: string;
  legalAddress?: string;
  postalAddress?: string;
  kpp?: string;
  ogrn?: string;
  bankName?: string;
  bankAccount?: string;
  bankCorrAccount?: string;
  bankBik?: string;
  email?: string;
  directorName?: string;
  createdAt: string;
}

export interface Warehouse {
  id: number;
  name: string;
  city: string;
  address?: string;
  contactPerson?: string;
  phone?: string;
}

export interface RailwayStation {
  id: number;
  name: string;
  code?: string;
  city?: string;
  region?: string;
  country: string;
  isActive: boolean;
  createdAt: string;
}

export interface Airport {
  id: number;
  name: string;
  iataCode?: string;
  icaoCode?: string;
  city?: string;
  region?: string;
  country: string;
  isActive: boolean;
  createdAt: string;
}

// RouteApplication (связующая таблица между Route и Application)
export interface RouteApplication {
  id: number;
  routeId: number;
  applicationId: number;
  application: Application;
  route: Route;
}

// Маршруты
export interface Route {
  id: number;
  number: string;
  transportType: TransportType;
  status: RouteStatus;
  loadingPointType: 'warehouse' | 'shipper';
  loadingPointId: number;
  unloadingPointType: 'warehouse' | 'application' | 'consignee';
  unloadingPointId: number;
  destinationType: 'warehouse' | 'application' | 'consignee';
  isDeliveryToConsignee: boolean;
  loadingAddress?: string;
  unloadingAddress?: string;
  loadingCity?: string | null;
  unloadingCity?: string | null;
  loadingContactPerson?: string;
  loadingPhone?: string;
  unloadingContactPerson?: string;
  unloadingPhone?: string;
  plannedLoadingDate?: string;
  plannedUnloadingDate?: string;
  actualLoadingDate?: string;
  actualUnloadingDate?: string;
  currentLocationLat?: number;
  currentLocationLng?: number;
  currentLocationAddress?: string;
  currentLocationUpdatedAt?: string;
  totalAmount?: number;
  paymentMethod?: PaymentMethod;
  prepaymentCondition?: string;
  prepaymentPercent?: number;
  prepaymentAmount?: number;
  prepaymentDelayDays?: number;
  prepaymentStartDate?: string;
  mainPaymentAmount?: number;
  mainPaymentDelayDays?: number;
  mainPaymentStartDate?: string;
  aggregatedStatus?: {
    overallStatus: 'OPEN' | 'PARTIAL' | 'OK' | 'OVERDUE';
    payableStatus?: string;
    receivableStatus?: string;
  };
  excludeFromCalculation?: boolean;
  executor?: string;
  isCompanyRoute?: boolean;
  income?: number;
  clientId?: number;
  client?: Client;
  expeditorId?: number;
  expeditor?: Expeditor;
  shipperId?: number;
  shipper?: Shipper;
  consigneeId?: number;
  consignee?: Consignee;
  driverId?: number;
  driver?: Driver;
  vehicleId?: number;
  vehicle?: Vehicle;
  trailerVehicleId?: number | null;
  trailerVehicle?: Vehicle | null;
  carrierId?: number;
  carrier?: Carrier;
  responsibleLogistId?: number | null;
  responsibleLogist?: MiniUser | null;
  wagonNumber?: string;
  departureStation?: string;
  arrivalStation?: string;
  flightNumber?: string;
  departureAirport?: string;
  arrivalAirport?: string;
  createdBy: number;
  createdAt: string;
  updatedAt: string;
  applications: RouteApplication[];
  cargo: ApplicationCargo[];
  events: RouteEvent[];
  documents: Document[];
  loadingDocuments?: LoadingDocument[];
  transportDocuments?: TransportDocument[];
  payments?: Payment[];
  loadingWarehouse?: Warehouse;
  unloadingWarehouse?: Warehouse;
  carrierContractRequestNumber?: string;
  carrierContractRequestDate?: string;
  carrierContractRequestFile?: string;
  /** Договор с перевозчиком (если отдан с API); иначе достаточно carrierContractRequest* на маршруте. */
  carrierAgreement?: { number?: string | null; date?: string | null } | null;
  additionalExpenses?: RouteAdditionalExpense[];
  /**
   * Цепочка стопов (GET /routes/:id и др. с include).
   * Нужна для направления и UI без отдельного запроса stops.
   */
  stops?: Array<{ city?: string | null; address?: string | null; position?: number | null }> | null;
}

export enum TransportType {
  AUTO = 'auto',
  RAILWAY = 'railway',
  AVIATION = 'aviation'
}

export enum RouteStatus {
  CREATED = 'created',
  IN_TRANSIT = 'in_transit',
  READY_FOR_COMPLETION = 'ready_for_completion',
  ISSUES = 'issues',
  COMPLETED = 'completed',
  CANCELLED = 'cancelled'
}

export enum PaymentMethod {
  CASH = 'cash',
  FUEL = 'fuel',
  NON_CASH_VAT = 'non_cash_vat',
  NON_CASH_NO_VAT = 'non_cash_no_vat'
}

export enum PaymentStatus {
  NOT_PAID = 'not_paid',
  PARTIALLY_PAID = 'partially_paid',
  PAID = 'paid'
}

export interface CreateRouteRequest {
  transportType: TransportType;
  loadingPointType: 'warehouse' | 'shipper';
  loadingPointId: number;
  unloadingPointType: 'warehouse' | 'application' | 'consignee';
  unloadingPointId: number;
  destinationType?: 'warehouse' | 'application' | 'consignee';
  isDeliveryToConsignee?: boolean;
  loadingAddress?: string;
  unloadingAddress?: string;
  loadingCity?: string;
  unloadingCity?: string;
  loadingContactPerson?: string;
  loadingPhone?: string;
  unloadingContactPerson?: string;
  unloadingPhone?: string;
  plannedLoadingDate?: string;
  plannedUnloadingDate?: string;
  totalAmount?: number;
  paymentMethod?: PaymentMethod;
  prepaymentCondition?: string;
  prepaymentPercent?: number;
  prepaymentDelayDays?: number;
  mainPaymentAmount?: number;
  mainPaymentDelayDays?: number;
  clientId?: number;
  expeditorId?: number;
  shipperId?: number;
  consigneeId?: number | null;
  responsibleLogistId?: number | null;
  driverId?: number;
  vehicleId?: number;
  trailerVehicleId?: number | null;
  carrierId?: number;
  wagonNumber?: string;
  departureStation?: string;
  arrivalStation?: string;
  flightNumber?: string;
  departureAirport?: string;
  arrivalAirport?: string;
  cargoIds: number[];
  isCompanyRoute?: boolean;
  executor?: string;
  carrierContractRequestNumber?: string;
  carrierContractRequestDate?: string;
  carrierContractRequestFile?: string;
  additionalExpenses?: Array<{
    description: string;
    amount: number;
  }>;
}

export interface RouteAdditionalExpense {
  id: number;
  routeId: number;
  description: string;
  amount: number;
  createdAt: string;
}

// Грузы
export interface ApplicationCargo {
  id: number;
  applicationId: number;
  application: Application;
  name: string;
  description?: string;
  quantity: number;
  /** Сумма аллокаций по всем маршрутам (из GET /routes/cargo/available). */
  allocatedQuantityTotal?: number;
  /** Доступно к распределению: quantity − аллокации (с учётом excludeRouteId при редактировании). */
  availableQuantity?: number;
  weightPerItem: number;
  lengthM?: number;
  widthM?: number;
  heightM?: number;
  totalWeightKg: number;
  volumeM3?: number;
  packagingType?: string;
  cargoCharacter?: string;
  status: CargoStatus;
  currentRouteId?: number;
  currentRoute?: Route;
  currentWarehouseId?: number;
  currentWarehouse?: Warehouse;
  deliveredDate?: string;
  createdAt: string;
}

export enum CargoStatus {
  IN_APPLICATION = 'in_application',
  IN_ROUTE = 'in_route',
  ON_WAREHOUSE = 'on_warehouse',
  DELIVERED = 'delivered'
}

// События маршрута
export interface RouteEvent {
  id: number;
  routeId: number;
  eventType: string;
  description?: string;
  locationLat?: number;
  locationLng?: number;
  eventDate: string;
  createdBy: number;
  createdAt: string;
  createdByUser?: {
    id: number;
    username?: string | null;
    email?: string | null;
    firstName?: string | null;
    lastName?: string | null;
    role?: { name: string | null } | null;
  };
}

// Документы
export interface Document {
  id: number;
  templateId?: number;
  template?: DocumentTemplate;
  documentNumber?: string;
  documentType: string;
  applicationId?: number;
  application?: Application;
  routeId?: number;
  route?: Route;
  cargoId?: number;
  cargo?: ApplicationCargo;
  entityType?: 'driver' | 'vehicle' | string | null;
  entityId?: number | null;
  stage?: 'loading' | 'unloading' | 'other';
  comment?: string;
  fileType?: 'file' | 'photo';
  customDocumentName?: string | null;
  customDocumentCategory?: string | null;
  version: number;
  content?: Record<string, any>;
  filePath?: string;
  isReceived: boolean;
  isSigned: boolean;
  isOriginalTN?: boolean;
  isPrimaryDocs?: boolean;
  locationDescription?: string;
  createdBy: number;
  createdAt: string;
  /** Дата получения документа (для триггеров обязательств). Опционально. */
  receivedDate?: string | null;
  downloadCount?: number;
  lastDownloadedAt?: string | null;
}

export interface DocumentTemplate {
  id: number;
  name: string;
  type: string;
  templateContent?: string;
  variables?: Record<string, any>;
  isActive: boolean;
  createdAt: string;
}

// Уведомления
export interface Notification {
  id: number;
  userId: number;
  type: 'info' | 'warning' | 'error' | 'success';
  priority?: 'CRITICAL' | 'HIGH' | 'NORMAL' | 'LOW';
  taskId?: number | null;
  title?: string;
  message?: string;
  data?: Record<string, any>;
  isRead: boolean;
  sentVia?: 'system' | 'email' | 'sms';
  sentAt?: string;
  createdAt: string;
}

export interface NotificationSettings {
  id: number;
  userId: number;
  emailEnabled: boolean;
  smsEnabled: boolean;
  notificationTypes: {
    newApplications: boolean;
    statusChanges: boolean;
    routeUpdates: boolean;
    warehouseOperations: boolean;
    documentIssues: boolean;
    delays: boolean;
  };
  schedule: 'immediate' | 'daily' | 'weekly';
  createdAt: string;
  updatedAt: string;
}

// Пагинация
export interface PaginationParams {
  page?: number;
  limit?: number;
}

export interface PaginationResponse<T> {
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

// Фильтры
export type ApplicationListScope = 'active' | 'archive';

export type ApplicationDateField =
  | 'applicationDate'
  | 'plannedLoadingDate'
  | 'plannedUnloadingDate'
  | 'actualLoadingDate'
  | 'actualUnloadingDate';

export interface ApplicationFilters {
  scope?: ApplicationListScope;
  status?: ApplicationStatus | string;
  clientId?: number | string;
  responsibleLogistId?: number | string;
  direction?: string;
  dateFrom?: string;
  dateTo?: string;
  dateField?: ApplicationDateField;
  source?: 'internal' | 'external';
  activeOnly?: boolean;
}

export type RouteListScope = 'active' | 'archive';

export interface RouteFilters {
  scope?: RouteListScope;
  transportType?: TransportType;
  status?: RouteStatus;
  dateFrom?: string;
  dateTo?: string;
  applicationId?: number;
  companyOnly?: boolean;
  responsibleLogistId?: number;
  carrierId?: number;
  clientId?: number;
  driverId?: number;
  vehicleId?: number;
}

export interface CargoFilters {
  status?: CargoStatus[] | CargoStatus;
  applicationId?: number;
  routeId?: number;
  warehouseId?: number;
  dateFrom?: string;
  dateTo?: string;
  search?: string;
  page?: number;
  limit?: number;
}

// Отчеты и дашборды
export interface DashboardData {
  activeApplicationsCount: number;
  cargoInTransitCount: number;
  warehouseOccupancy: Record<string, number>;
  delayStatistics: {
    onTime: number;
    delayed: number;
  };
  recentEvents: RouteEvent[];
}

export interface ReportParams {
  type: string;
  dateFrom?: string;
  dateTo?: string;
  filters?: Record<string, any>;
  format?: 'excel' | 'pdf' | 'csv';
}

// Дополнительные типы для модуля грузов

// История перемещений груза
export interface CargoHistoryEvent {
  id: number;
  cargoId: number;
  eventType: 'created' | 'route_assigned' | 'warehouse_received' | 'delivered' | 'status_changed';
  description: string;
  eventDate: string;
  performedBy: string;
  fromLocation?: string;
  toLocation?: string;
  routeId?: number;
  warehouseId?: number;
  oldValue?: any;
  newValue?: any;
}

// Ответ API для списка грузов с пагинацией
export interface CargoResponse {
  cargo: ApplicationCargo[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

// Запрос на обновление статуса груза
export interface UpdateCargoStatusRequest {
  status: CargoStatus;
  routeId?: number;
  warehouseId?: number;
  deliveredDate?: string;
  notes?: string;
}

// Расширенная информация о грузе с полными связями
export interface CargoDetails extends ApplicationCargo {
  application: Application & {
    client: Client;
    expeditor?: Expeditor;
    shipper?: Shipper;
    consignee?: Consignee;
  };
  currentRoute?: Route & {
    applications: Array<Application & { client: Client }>;
  };
  currentWarehouse?: Warehouse;
}

// Константы статусов грузов для UI
export const CARGO_STATUS_LABELS = {
  [CargoStatus.IN_APPLICATION]: 'В заявке',
  [CargoStatus.IN_ROUTE]: 'В маршруте',
  [CargoStatus.ON_WAREHOUSE]: 'На складе',
  [CargoStatus.DELIVERED]: 'Доставлен'
} as const;

// Цвета для статусов грузов
export const CARGO_STATUS_COLORS = {
  [CargoStatus.IN_APPLICATION]: 'blue',
  [CargoStatus.IN_ROUTE]: 'orange',
  [CargoStatus.ON_WAREHOUSE]: 'purple',
  [CargoStatus.DELIVERED]: 'green'
} as const;

export interface CargoHistoryEntry {
  id: number;
  cargoId: number;
  /** Сырое действие из audit_log */
  action?: string;
  actionType: 'status_change' | 'location_change' | 'creation' | 'update' | 'deletion';
  description: string;
  oldValue?: any;
  newValue?: any;
  userName?: string;
  createdAt: string;
  eventDate?: string;
  /** Маршрут из newValue/oldValue (для ALLOCATED / UNALLOCATED / ROUTE_DELETED и legacy). */
  routeId?: number | null;
  /** Есть ли сейчас RouteCargoAllocation для пары (routeId, cargoId). */
  isActual?: boolean;
  routeInfo?: {
    id: number;
    loadingAddress?: string;
    unloadingAddress?: string;
    plannedLoadingDate?: string;
    plannedUnloadingDate?: string;
    actualLoadingDate?: string;
    actualUnloadingDate?: string;
    status: string;
  };
} 

// Финансовые типы
export interface FinancialStats {
  activeApplications: number;
  totalRevenue: number;
  totalCosts: number;
  profit: number;
  pendingPayments: number;
  overduePayments: number;
  todayPayments: number;
  tomorrowPayments: number;
  unsignedDocs: number;
}

export interface ApplicationFinance {
  id: number;
  internalNumber: string;
  externalNumber: string;
  applicationDate: string;
  client: {
    id: number;
    name: string;
    address?: string;
    contactPerson?: string;
    phone?: string;
    inn?: string;
    roles?: string[];
    isActive: boolean;
    createdAt: string;
    updatedAt: string;
    notes?: string;
  };
  status: ApplicationStatus;
  totalCostWithVAT?: number;
  primaryDocsStatus?: string;
  primaryDocsSignedDate?: string;
  partnerPaymentStatus?: string;
  partnerPaymentAmount?: number;
  partnerPaymentDate?: string;
  routeCosts: number;
  totalPayments: number;
  profit: number;
  routes: any[];
  payments: any[];
}

export interface RouteFinance {
  id: number;
  routeNumber: string;
  applicationId: number;
  transportType: 'AUTO' | 'RAIL' | 'AIR';
  carrierName: string;
  executor: 'OUR_COMPANY' | 'PARTNER' | 'EXTERNAL';
  routeCost: number;
  prepaymentPercent: number;
  prepaymentAmount: number;
  prepaymentDelayDays: number;
  prepaymentStartDate?: string;
  prepaymentDueDate?: string;
  finalPaymentPercent: number;
  finalPaymentAmount: number;
  finalPaymentDelayDays: number;
  finalPaymentStartDate?: string;
  finalPaymentDueDate?: string;
  excludeFromCalculation: boolean;
  daysUntilPrepayment: number;
  daysUntilFinalPayment: number;
  isPrepaymentOverdue: boolean;
  isFinalPaymentOverdue: boolean;
}

export interface Payment {
  id: number;
  routeId?: number;
  paymentType: 'PREPAYMENT' | 'FINAL_PAYMENT';
  amountToPay: number;
  amountPaid: number;
  paymentDate?: string;
  status: 'PENDING' | 'PARTIAL' | 'PAID' | 'OVERDUE';
  daysToPayment?: number;
  createdAt: string;
  updatedAt: string;
  route?: {
    id: number;
    transportType: string;
    carrier?: {
      id: number;
      name: string;
    };
  };
}

export interface LoadingDocument {
  id: number;
  routeId: number;
  loadingDate: string;
  photos: string[];
  invoiceDocument?: string;
  uploadedBy: number;
  uploadedByUser?: {
    id: number;
    username: string;
    email: string;
  };
  uploadedAt: string;
}

export interface TransportDocument {
  id: number;
  routeId: number;
  deliveryType: 'MAIL' | 'EDO' | 'PERSONAL';
  trackingNumber?: string;
  receivedDate: string;
  documents: string[];
  status: 'RECEIVED' | 'PROCESSED';
  uploadedBy: number;
  uploadedByUser?: {
    id: number;
    username: string;
    email: string;
  };
  uploadedAt: string;
} 